/**
 * Status Change Dropdown Component
 *
 * Provides consistent "Change Status" dropdown for vulnerability status changes with:
 * - Triage, Open, Closed, Accept, Reject status options
 * - Modal dialog for "Reject" status with close reason selection
 * - Keyboard shortcuts (T/O/C/A/R)
 *
 * Migrated from ChangeRiskStatus component to standard action pattern.
 *
 * @example
 * ```tsx
 * <StatusChangeDropdown
 *   risks={selectedVulnerabilities}
 *   onUpdateRisk={handleUpdateRisk}
 *   getRiskStatusLabel={getRiskStatusLabel}
 *   disabled={false}
 * />
 * ```
 *
 * @module components/actions
 */

import { XCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { ACTION_ICONS } from '@/components/actions/constants/actionIcons';
import { Dropdown } from '@/components/Dropdown';
import { Modal } from '@/components/Modal';
import { getBaseRiskStatusOptions } from '@/components/ui/RiskDropdown';
import { useVulnerabilityStatusMode } from '@/hooks/useVulnerabilityStatusMode';
import { useAuth } from '@/state/auth';
import { Risk, RiskDeletedSubStatus, RiskStatus } from '@/types';
import { cn } from '@/utils/classname';
import { getStatusLabel } from '@/utils/vulnerabilityStatus.util';

/**
 * Props for StatusChangeDropdown component
 *
 * @template T - Type of risk items (must have key and status, typically Risk)
 */
export interface StatusChangeDropdownProps<
  T extends { key: string; status: string } = Risk,
> {
  /** Risks to change status for */
  risks: T[];
  /** Handler for updating risk status */
  onUpdateRisk: (risk: T, comment?: string) => Promise<void>;
  /** Function to parse risk status */
  getRiskStatusLabel?: (status: string) => { status: string; severity: string };
  /** Disable the dropdown */
  disabled?: boolean;
  /** Hide label (for compact views) */
  hideLabel?: boolean;
  /** Test ID for dropdown */
  'data-testid'?: string;
}

/**
 * Get status options for dropdown based on user type and mode
 * @param isPraetorianUser - Whether user is a Praetorian user
 * @param mode - The current vulnerability status mode
 * @returns Status options including Pending for Praetorian users
 */
const getStatusOptions = (
  isPraetorianUser: boolean,
  mode: 'legacy' | 'new'
) => [
  ...getBaseRiskStatusOptions(isPraetorianUser, mode),
  {
    label: getStatusLabel(RiskStatus.Deleted, mode),
    value: 'reject',
    icon: <XCircleIcon className="size-4" />,
    count: '⌥ R',
  },
];

/**
 * Close reason options for reject modal
 */
const closeReasons = [
  {
    label: 'False Positive',
    value: RiskDeletedSubStatus.FalsePositive,
    description:
      'This risk is deemed to be a false positive or not valid, and no further action will be taken.',
  },
  {
    label: 'Out of Scope',
    value: RiskDeletedSubStatus.OutOfScope,
    description:
      'This risk is out of scope and will not be addressed or tracked.',
  },
  {
    label: 'Duplicate',
    value: RiskDeletedSubStatus.Duplicate,
    description:
      'This risk is a duplicate of another risk and will not be addressed or tracked.',
  },
  {
    label: 'Other',
    value: RiskDeletedSubStatus.Other,
    description: 'There is something else that is not listed above.',
  },
];

/**
 * Status Change Dropdown Component
 *
 * Renders a dropdown with status change options for vulnerabilities.
 * Opens modal for "Reject" status to collect close reason.
 *
 * @template T - Type of risk items (must have key and status)
 * @param props - Component props
 * @returns Dropdown component with modal
 */
export function StatusChangeDropdown<
  T extends { key: string; status: string } = Risk,
>({
  risks,
  onUpdateRisk,
  getRiskStatusLabel: _getRiskStatusLabel,
  disabled = false,
  hideLabel = false,
  'data-testid': testId = 'status-change-dropdown',
}: StatusChangeDropdownProps<T>) {
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const { isPraetorianUser } = useAuth();
  const { mode } = useVulnerabilityStatusMode();

  // Get status options filtered by user type with mode-aware labels
  const statusOptions = getStatusOptions(isPraetorianUser, mode);

  const handleStatusChange = async (selectedValue: string) => {
    if (selectedValue === 'reject') {
      setIsCloseModalOpen(true);
      return;
    }

    try {
      await Promise.all(
        risks.map(async risk => {
          const currentSeverity = risk.status[1];
          const substatus = risk.status.slice(2); // Preserve any substatus
          const combinedStatus = `${selectedValue}${currentSeverity}${substatus}`;
          const updatedRisk = { ...risk, status: combinedStatus };
          await onUpdateRisk(updatedRisk);
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <div data-testid={testId} className="w-fit">
        <Dropdown
          options={statusOptions}
          onSelect={option => handleStatusChange(option.value as string)}
          styleType="rounded"
          size="xxs"
          icon={<ACTION_ICONS.STATUS_CHANGE className="size-4" />}
          placeholder={hideLabel ? undefined : 'Change Status'}
          disabled={disabled}
          className="[&_span:nth-child(3)]:hidden"
        />
      </div>
      <CloseModal
        open={isCloseModalOpen}
        onClose={() => setIsCloseModalOpen(false)}
        risks={risks}
        onUpdateRisk={onUpdateRisk}
      />
    </>
  );
}

/**
 * Props for CloseModal component
 *
 * @template T - Type of risk items (must have key and status)
 */
interface CloseModalProps<T extends { key: string; status: string }> {
  open: boolean;
  onClose: () => void;
  risks: T[];
  onUpdateRisk: (risk: T, comment?: string) => Promise<void>;
}

/**
 * Modal for selecting close reason when rejecting risks
 *
 * @template T - Type of risk items (must have key and status)
 */
const CloseModal = <T extends { key: string; status: string }>({
  open,
  onClose,
  risks,
  onUpdateRisk,
}: CloseModalProps<T>) => {
  const [selectedCloseReason, setSelectedCloseReason] = useState<string>('');

  const handleCloseConfirm = async () => {
    if (!selectedCloseReason) return;

    try {
      const closeReason = closeReasons.find(
        r => r.value === selectedCloseReason
      );

      await Promise.all(
        risks.map(async risk => {
          const updatedRisk = {
            ...risk,
            status: `${RiskStatus.Deleted}${risk.status[1]}${closeReason?.value}`,
          };
          await onUpdateRisk(updatedRisk);
        })
      );

      onClose();
      setSelectedCloseReason('');
    } catch {
      console.error('Failed to update risk status');
    }
  };

  return (
    <Modal
      title="Reject Risk"
      open={open}
      onClose={() => {
        onClose();
        setSelectedCloseReason('');
      }}
      footer={{
        text: 'Confirm',
        onClick: handleCloseConfirm,
        disabled: !selectedCloseReason,
      }}
      className="py-0"
    >
      <div className="space-y-4 py-2 pt-0">
        <hr className="border-chariot-stroke w-full" />
        <p className="text-chariot-text-secondary text-sm">
          {risks.length === 1
            ? 'Select a reason for closing this risk:'
            : `Select a reason for closing these ${risks.length} risks:`}
        </p>
        <div className="space-y-2">
          {closeReasons.map((reason, key) => (
            <Radio
              key={key}
              selected={selectedCloseReason}
              label={reason.label}
              description={reason.description}
              value={reason.value}
              onChange={setSelectedCloseReason}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};

/**
 * Radio button component for close reason selection
 */
const Radio = ({
  selected,
  label,
  description,
  value,
  onChange,
}: {
  selected: string;
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const checked = selected === value;
  return (
    <label
      className={cn(
        'border-chariot-stroke hover:bg-chariot-background-secondary flex items-start gap-3 rounded-md border p-4',
        checked && 'bg-chariot-background-secondary'
      )}
    >
      <input
        type="radio"
        name="deletedStatus"
        checked={checked}
        onChange={() => onChange(value)}
        className="mt-1"
      />
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-chariot-text-secondary text-sm">{description}</div>
      </div>
    </label>
  );
};
